import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import CarouselCard from './CarouselCard';
import { getStoreByKey } from './store/CarouselStore';
import { CarouselFieldsEnum } from './types/CarouselTypes';
import { useSortedModels } from './useSortedModels';

const CarouselSlider = ({ uniqId }: { uniqId: string }) => {
  const { direction, options } = useStore(getStoreByKey(uniqId));
  const slider = useRef<HTMLDivElement>(null);
  const [control, setControls] = useState<'left' | 'right' | 'both'>('right');

  const checkControls = () => {
    const container = slider.current;
    if (!container) return;
    setTimeout(() => {
      if (container.scrollLeft < 40) {
        setControls('right');
      } else if (
        container.scrollLeft >=
        container.scrollWidth - (container.clientWidth + 40)
      ) {
        setControls('left');
      } else {
        setControls('both');
      }
    }, 700);
  };

  const slideRight = () => {
    const container = slider.current;
    container.scrollLeft =
      container.scrollLeft + container.lastElementChild.clientWidth;
    checkControls();
  };

  const slideLeft = () => {
    const container = slider.current;
    container.scrollLeft =
      container.scrollLeft - container.lastElementChild.clientWidth;
    checkControls();
  };

  useEffect(() => {
    document.addEventListener('touchend', () => checkControls());

    return () => {
      document.removeEventListener('touchend', () => checkControls());
    };
  }, []);

  const sortedModels = useSortedModels(uniqId);

  return (
    <div className="relative">
      <div
        ref={slider}
        className={`gap-x-4 px-4 scrollbar-hide pb-4 overflow-x-auto overflow-y-hidden scroll-smooth snap-x box-border grid grid-flow-col ${direction === 'vertical' ? 'sm:flex sm:flex-col' : ''}`}
      >
        {sortedModels.map((model) => (
          <CarouselCard uniqId={uniqId} key={model.id} model={model} />
        ))}
      </div>
      {direction === 'horizontal' || window.innerWidth < 640 ? (
        <div
          className={`absolute z-50 transform -translate-y-1/2 w-[calc(100%-1rem)] left-2 justify-between flex gap-2 pointer-events-none md:right-12 ${!options || options?.fields.includes(CarouselFieldsEnum.CHANGE_TRIM) ? 'top-48' : 'top-1/2'}`}
        >
          <button
            className={`pointer-events-auto bg-white rounded-full shadow-md ${['left', 'both'].includes(control) ? '' : 'opacity-0 pointer-events-none md:pointer-events-auto'}`}
            aria-label="Gå till vänster"
            onClick={() => slideLeft()}
            disabled={!['left', 'both'].includes(control)}
          >
            <svg
              width="37"
              height="37"
              viewBox="0 0 37 37"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                opacity="0.2"
                cx="18.5"
                cy="18.5"
                r="18"
                fill="#fff"
              ></circle>
              <path
                d="M20 23L15.5 18.5L20 14"
                stroke="#000"
                strokeWidth="1.5"
              ></path>
            </svg>
          </button>
          <button
            className={`pointer-events-auto bg-white rounded-full shadow-md ${['right', 'both'].includes(control) ? '' : 'opacity-0 pointer-events-none md:pointer-events-auto'}`}
            aria-label="Gå till höger"
            onClick={() => slideRight()}
            disabled={!['right', 'both'].includes(control)}
          >
            <svg
              width="37"
              height="37"
              viewBox="0 0 37 37"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                opacity="0.2"
                cx="18.5"
                cy="18.5"
                r="18"
                fill="none"
              ></circle>
              <path
                d="M17 23L21.5 18.5L17 14"
                stroke="#000"
                strokeWidth="1.5"
              ></path>
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default CarouselSlider;
